import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { apiClientPublic } from "src/api/apiService";
import { FeeInfo, FeeScriptingEnum } from "../types";
import { normaliseDate } from "src/api/apiHelper";
import {
  validateJourneyId,
  validateMeterNumber,
  validatePostcode,
  validateScheduleDate,
} from "../publicFormValidations";
import type {
  FormData,
  FormErrors,
  AddressLookupResponse,
} from "../RemoveSupplyPublicForm";
import { SupplyConfigurationType } from "./type";

export const useRemoveSupplyPublicForm = (
  supplyConfiguration: SupplyConfigurationType
) => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const { initialFormData, createSetFormDataAction, getSuccessUrl } =
    supplyConfiguration;
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [address, setAddress] = useState<string>("");
  const [feeInfo, setFeeInfo] = useState<FeeInfo | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showError, setShowError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const handleJourneyIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, "").slice(0, 7);
    setFormData({ ...formData, journeyId: value });
  };

  const handlePostcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, "").slice(0, 4);
    setFormData({ ...formData, postcode: value });
  };

  const handleJourneyIdBlur = () => {
    setTouched({ ...touched, journeyId: true });
    setErrors({ ...errors, journeyId: validateJourneyId(formData.journeyId) });
  };

  const handlePostcodeBlur = () => {
    setTouched({ ...touched, postcode: true });
    setErrors({ ...errors, postcode: validatePostcode(formData.postcode) });
  };

  const handleCheckDetails = async () => {
    const journeyIdError = validateJourneyId(formData.journeyId);
    const postcodeError = validatePostcode(formData.postcode);

    // Update errors and touched state in single calls
    setErrors({
      journeyId: journeyIdError,
      postcode: postcodeError,
    });
    setTouched({ journeyId: true, postcode: true });

    // Early return if validation errors exist
    if (journeyIdError || postcodeError) {
      setShowError(false);
      return;
    }

    setShowError(false);
    setLoading(true);

    try {
      const response = await apiClientPublic.get<AddressLookupResponse>(
        "/address/lookup",
        {
          params: {
            id: formData.journeyId,
            postcode: formData.postcode,
          },
        }
      );

      // Destructure all needed data from response
      const {
        address: addressData = "",
        service_identifier: idValue = "",
        fee_scripting,
        db_fee,
        mp_fee,
      } = response.data;

      // Update all related state together
      setAddress(addressData);

      // Set the correct field based on idField
      setFormData(createSetFormDataAction(idValue));

      // Set fee information if available
      if (fee_scripting) {
        setFeeInfo({
          feeScripting: fee_scripting as FeeScriptingEnum,
          dbFee: db_fee,
          mpFee: mp_fee,
        });
      }
    } catch {
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleFinalSubmit = async () => {
    const meterNumberError = validateMeterNumber(formData.meterNumber);
    const scheduleDateError = validateScheduleDate(formData.scheduleDate);

    setErrors((prev) => ({
      ...prev,
      meterNumber: meterNumberError,
      scheduleDate: scheduleDateError,
    }));

    setTouched((prev) => ({
      ...prev,
      meterNumber: true,
      scheduleDate: true,
    }));

    if (!meterNumberError && !scheduleDateError) {
      setLoading(true);
      try {
        const captchaToken = await recaptchaRef.current?.executeAsync();
        recaptchaRef.current?.reset();
        if (captchaToken && code) {
          await apiClientPublic.post("/abolishment/verify", {
            code,
            date: normaliseDate(formData.scheduleDate, null),
            reCaptcha: captchaToken,
          });
          navigate(getSuccessUrl());
        } else {
          console.log("no captcha");
        }
      } catch {
        console.log("remove supply submission failed");
      } finally {
        setLoading(false);
      }
    }
  };

  return {
    recaptchaRef,
    formData,
    setFormData,
    address,
    feeInfo,
    errors,
    touched,
    showError,
    loading,
    handleJourneyIdChange,
    handlePostcodeChange,
    handleJourneyIdBlur,
    handlePostcodeBlur,
    handleCheckDetails,
    handleFinalSubmit,
  };
};
